import React, { useEffect, useState, useRef } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";

const LEAD_MIN = 5; // nudge 5 min before slot
const STORAGE_PREFIX = "adv_nudge_";
const FIRED_PREFIX = "adv_nudge_fired_";

function storageKey(id) { return `${STORAGE_PREFIX}${id}`; }
function firedKey(id) { return `${FIRED_PREFIX}${id}`; }

function isEnabled(id) {
  try { return localStorage.getItem(storageKey(id)) === "1"; } catch { return false; }
}
function setEnabled(id, v) {
  try {
    if (v) localStorage.setItem(storageKey(id), "1");
    else localStorage.removeItem(storageKey(id));
  } catch {}
}
function hasFired(id) {
  try { return localStorage.getItem(firedKey(id)) === "1"; } catch { return false; }
}
function markFired(id) {
  try { localStorage.setItem(firedKey(id), "1"); } catch {}
}

// Shared global scheduler — runs one interval regardless of how many toggles mount
let schedulerStarted = false;
const _consultMap = new Map(); // id -> { slotDate, lawyer_name, type }

function fireNudge(id, info) {
  if (hasFired(id)) return;
  const body = `Your ${info.type} consultation with ${info.lawyer_name} starts in ~${LEAD_MIN} min.`;
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification("Advoset — Consultation reminder", {
        body,
        icon: "/favicon.ico",
        tag: `consult-${id}`,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = `/consultations/${id}`;
      };
    }
  } catch {}
  // Also toast, in case permission was revoked after enabling
  toast.info("Consultation starts in 5 mins", { description: body, duration: 10000 });
  markFired(id);
}

function startScheduler() {
  if (schedulerStarted) return;
  schedulerStarted = true;
  const tick = () => {
    const now = Date.now();
    _consultMap.forEach((info, id) => {
      if (!isEnabled(id)) return;
      if (hasFired(id)) return;
      const diffMin = (info.slotDate.getTime() - now) / 60000;
      if (diffMin <= LEAD_MIN && diffMin > -1) {
        fireNudge(id, info);
      }
    });
  };
  setInterval(tick, 30_000); // every 30s
  tick();
}

export default function NudgeToggle({ consult, slotDate }) {
  const [on, setOn] = useState(() => isEnabled(consult.id));
  const registeredRef = useRef(false);

  // Register this consult in the shared scheduler map
  useEffect(() => {
    if (!slotDate) return;
    _consultMap.set(consult.id, {
      slotDate,
      lawyer_name: consult.lawyer_name || "your lawyer",
      type: consult.consultation_type || "chat",
    });
    registeredRef.current = true;
    startScheduler();
    return () => {
      _consultMap.delete(consult.id);
    };
  }, [consult.id, slotDate, consult.lawyer_name, consult.consultation_type]);

  const toggle = async () => {
    if (on) {
      setEnabled(consult.id, false);
      setOn(false);
      toast("Reminder turned off");
      return;
    }
    // Enabling — request permission if needed
    if (!("Notification" in window)) {
      toast.error("Your browser doesn't support notifications");
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") {
      try { perm = await Notification.requestPermission(); } catch { perm = "denied"; }
    }
    if (perm !== "granted") {
      toast.error("Enable notifications in your browser to get reminders");
      return;
    }
    setEnabled(consult.id, true);
    setOn(true);
    const minsAway = Math.max(0, Math.round((slotDate.getTime() - Date.now()) / 60000) - LEAD_MIN);
    toast.success(
      minsAway > 0
        ? `We'll nudge you in ${minsAway} min (${LEAD_MIN} min before the consult)`
        : `We'll nudge you right before the consult`
    );
  };

  // Slot too close to still be worth toggling (already past lead time → 2 min buffer)
  if (!slotDate) return null;
  const tooLate = slotDate.getTime() - Date.now() < (LEAD_MIN - 2) * 60 * 1000;
  if (tooLate) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={`nudge-toggle-${consult.id}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-xs font-semibold border transition-colors ${
        on
          ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]"
          : "border-slate-200 text-slate-600 hover:border-[#0A2540]/40 hover:text-[#0A2540]"
      }`}
      title={on ? "You'll be reminded 5 min before" : "Get a 5-min-before reminder"}
    >
      {on ? <BellRing size={13} /> : <Bell size={13} />}
      <span className="hidden sm:inline">{on ? "Reminder on" : "Nudge me 5 min before"}</span>
      <span className="sm:hidden">{on ? "On" : "Nudge me"}</span>
      {on && <BellOff size={10} className="opacity-50 ml-0.5" aria-hidden />}
    </button>
  );
}
